/**
 * SiteBaseSchema — sitewide, route-aware JSON-LD injector.
 *
 * Emits three high-value schemas that Google, Perplexity, Claude and
 * Gemini all read when deciding whether to cite Central Rentals in
 * their answers:
 *
 *   1. Organization — logo, phone (780-405-2498), sameAs, foundingDate.
 *   2. WebSite      — with SearchAction so Google can show a search box
 *                     under the sitelink.
 *   3. BreadcrumbList — auto-derived from the URL path so every deep
 *                       page gets the "Home › Section › Page" trail.
 *
 * Sits BESIDE AutoSchema (which handles SoftwareApplication) so the
 * two together deliver the full sitewide schema baseline.
 *
 * Mounted ONCE at the App shell.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const PUBLIC = "https://centralrentals.ca";
const LOGO   = "https://centralrentals.ca/logo.png";
const PHONE  = "+1-780-405-2498";

const ORG_ID   = "crc-org-schema";
const SITE_ID  = "crc-site-schema";
const CRUMB_ID = "crc-breadcrumb-schema";

// Human-readable breadcrumb labels for the top-level route segments.
// Anything not listed here falls back to the segment's slug prettified
// (kebab-case → Title Case).
const SEGMENT_LABELS = {
  vs: "Alternatives",
  alternatives: "Alternatives",
  features: "Features",
  tools: "Free tools",
  answers: "Answers",
  blog: "Blog",
  pricing: "Pricing",
  integrate: "Integrations",
  "landlord-software": "Landlord software",
  "property-management": "Property management",
  "for": "For",
  "use-cases": "Use cases",
  best: "Best of",
  university: "University",
};

function prettify(slug) {
  const label = SEGMENT_LABELS[slug];
  if (label) return label;
  return decodeURIComponent(slug)
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function upsertScript(id, obj) {
  document.getElementById(id)?.remove();
  const s = document.createElement("script");
  s.id = id;
  s.type = "application/ld+json";
  s.text = JSON.stringify(obj);
  document.head.appendChild(s);
}

function orgSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${PUBLIC}/#organization`,
    name: "Central Rentals Canada",
    legalName: "Central Rentals Canada Inc.",
    alternateName: [
      "Central Rentals",
      "Central Rentals CA",
      "Central Rentals landlord platform",
      "Central Rentals property management platform",
    ],
    url: PUBLIC,
    logo: LOGO,
    description:
      "Canadian landlord platform and property management platform for landlords and property managers in all 13 provinces. Rent collection, provincial notices (N1-N13, TAL, RTDRS), tenant screening, and 1-click CRA T776 exports.",
    keywords: "landlord platform, property management platform, property management software Canada, landlord software Canada",
    foundingDate: "2024-01-01",
    email: "contact@centralrentals.ca",
    telephone: PHONE,
    // Editorial + accreditation signals boost E-E-A-T Expertise.
    publishingPrinciples: `${PUBLIC}/editorial-policy`,
    ownershipFundingInfo: `${PUBLIC}/about`,
    diversityPolicy: `${PUBLIC}/diversity`,
    correctionsPolicy: `${PUBLIC}/editorial-policy#corrections`,
    ethicsPolicy: `${PUBLIC}/editorial-policy#ethics`,
    contactPoint: [{
      "@type": "ContactPoint",
      telephone: PHONE,
      contactType: "customer support",
      areaServed: "CA",
      availableLanguage: ["en", "fr"],
      email: "contact@centralrentals.ca",
    }, {
      "@type": "ContactPoint",
      contactType: "privacy",
      email: "privacy@centralrentals.ca",
      areaServed: "CA",
    }, {
      "@type": "ContactPoint",
      contactType: "press",
      email: "press@centralrentals.ca",
      areaServed: "CA",
    }],
    address: {
      "@type": "PostalAddress",
      addressCountry: "CA",
    },
    sameAs: [
      "https://www.linkedin.com/company/central-rentals-canada",
      "https://x.com/CentralRentals",
      "https://www.facebook.com/centralrentalscanada",
      "https://alberta.ca/corporate-registry-search",
    ],
    // Every Canadian province + territory we operate in — signals Google
    // that Central Rentals is a legitimate service provider for local
    // searchers in every jurisdiction, which is a strong ranking factor
    // for the "property management software in [city/province]" cluster.
    areaServed: [
      { "@type": "AdministrativeArea", name: "Ontario",                   identifier: "CA-ON" },
      { "@type": "AdministrativeArea", name: "British Columbia",          identifier: "CA-BC" },
      { "@type": "AdministrativeArea", name: "Alberta",                   identifier: "CA-AB" },
      { "@type": "AdministrativeArea", name: "Québec",                    identifier: "CA-QC" },
      { "@type": "AdministrativeArea", name: "Manitoba",                  identifier: "CA-MB" },
      { "@type": "AdministrativeArea", name: "Saskatchewan",              identifier: "CA-SK" },
      { "@type": "AdministrativeArea", name: "Nova Scotia",               identifier: "CA-NS" },
      { "@type": "AdministrativeArea", name: "New Brunswick",             identifier: "CA-NB" },
      { "@type": "AdministrativeArea", name: "Newfoundland and Labrador", identifier: "CA-NL" },
      { "@type": "AdministrativeArea", name: "Prince Edward Island",      identifier: "CA-PE" },
      { "@type": "AdministrativeArea", name: "Yukon",                     identifier: "CA-YT" },
      { "@type": "AdministrativeArea", name: "Northwest Territories",     identifier: "CA-NT" },
      { "@type": "AdministrativeArea", name: "Nunavut",                   identifier: "CA-NU" },
    ],
    // Plan catalog so Google can render pricing snippets under the
    // Organization rich result. Prices mirror the paid tiers in
    // `pages/Pricing.jsx` (DIY / Starter / Growth / Enterprise, 2026).
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Central Rentals property management software plans",
      itemListElement: [
        { "@type": "Offer", name: "DIY",        price: "5",  priceCurrency: "CAD", url: `${PUBLIC}/pricing`, category: "1 unit — solo landlord" },
        { "@type": "Offer", name: "Starter",    price: "35", priceCurrency: "CAD", url: `${PUBLIC}/pricing`, category: "up to 10 units" },
        { "@type": "Offer", name: "Pro/Growth", price: "50", priceCurrency: "CAD", url: `${PUBLIC}/pricing`, category: "11-50 units" },
        { "@type": "Offer", name: "Enterprise", price: "80", priceCurrency: "CAD", url: `${PUBLIC}/pricing`, category: "50+ units" },
      ],
    },
    knowsAbout: [
      "Property management software Canada",
      "Landlord software Canada",
      "Residential Tenancy Act Ontario",
      "Residential Tenancy Act British Columbia",
      "Loi sur le TAL Québec",
      "Alberta Residential Tenancies Act",
      "Manitoba Residential Tenancies Act",
      "Saskatchewan Residential Tenancies Act",
      "N4 late-rent notice generator",
      "N12 landlord's own use notice",
      "CRA T776 rental income export",
      "Tenant screening Canada",
      "Rent collection e-Transfer PAD Klarna",
    ],
  };
}

// Additional LocalBusiness schema — makes Central Rentals eligible for
// Google Business Profile-adjacent local packs across every Canadian
// province, and gives Bing Places + Apple Business Connect a machine-
// readable NAP (name/address/phone) source of truth.
function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${PUBLIC}/#localbusiness`,
    name: "Central Rentals Canada",
    image: LOGO,
    url: PUBLIC,
    telephone: PHONE,
    email: "contact@centralrentals.ca",
    priceRange: "CA$5 - CA$80 / month",
    description:
      "Canadian property management software serving landlords and property management companies in every province and territory. Rent collection in CAD, provincial notices (N1-N13, RTB, TAL), tenant screening, CRA T776 exports.",
    address: {
      "@type": "PostalAddress",
      addressCountry: "CA",
    },
    areaServed: [
      { "@type": "Country", name: "Canada", "@id": "https://en.wikipedia.org/wiki/Canada" },
    ],
    // Cities we already publish landing pages for — signals local relevance
    // to Google when Canadians search "property management software near me".
    serviceArea: [
      "Toronto", "Ottawa", "Mississauga", "Brampton", "Hamilton",
      "Vancouver", "Victoria", "Surrey", "Burnaby", "Kelowna",
      "Calgary", "Edmonton", "Red Deer", "Lethbridge", "Airdrie",
      "Montréal", "Québec City", "Laval", "Gatineau", "Sherbrooke",
      "Winnipeg", "Brandon", "Steinbach", "Thompson",
      "Saskatoon", "Regina", "Moose Jaw", "Prince Albert",
      "Halifax", "Sydney", "Moncton", "Fredericton",
      "St. John's", "Charlottetown", "Yellowknife", "Whitehorse", "Iqaluit",
    ].map((city) => ({ "@type": "City", name: city, containedInPlace: { "@type": "Country", name: "Canada" } })),
    openingHoursSpecification: [{
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "20:00",
    }, {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday", "Sunday"],
      opens: "10:00",
      closes: "18:00",
    }],
    contactPoint: [{
      "@type": "ContactPoint",
      telephone: PHONE,
      contactType: "customer support",
      areaServed: "CA",
      availableLanguage: ["en", "fr"],
    }],
    hasMap: `${PUBLIC}/local`,
    sameAs: [
      "https://www.linkedin.com/company/central-rentals-canada",
      "https://x.com/CentralRentals",
      "https://www.facebook.com/centralrentalscanada",
    ],
  };
}

function siteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${PUBLIC}/#website`,
    url: PUBLIC,
    name: "Central Rentals Canada",
    publisher: { "@id": `${PUBLIC}/#organization` },
    inLanguage: ["en-CA", "fr-CA"],
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${PUBLIC}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

function breadcrumbSchema(pathname) {
  const segs = pathname.split("/").filter(Boolean);
  if (!segs.length) return null;
  const items = [{
    "@type": "ListItem",
    position: 1,
    name: "Home",
    item: PUBLIC + "/",
  }];
  let acc = "";
  segs.forEach((seg, i) => {
    acc += "/" + seg;
    items.push({
      "@type": "ListItem",
      position: i + 2,
      name: prettify(seg),
      item: PUBLIC + acc,
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

export default function SiteBaseSchema() {
  const { pathname } = useLocation();

  // Organization + WebSite are truly sitewide — inject once at mount
  // and only refresh if the block was somehow removed.
  useEffect(() => {
    upsertScript(ORG_ID, orgSchema());
    upsertScript(SITE_ID, siteSchema());
    return () => {
      document.getElementById(ORG_ID)?.remove();
      document.getElementById(SITE_ID)?.remove();
    };
  }, []);

  // Breadcrumb refreshes on every route change.
  useEffect(() => {
    const crumb = breadcrumbSchema(pathname);
    if (crumb) {
      upsertScript(CRUMB_ID, crumb);
    } else {
      document.getElementById(CRUMB_ID)?.remove();
    }
    return () => { document.getElementById(CRUMB_ID)?.remove(); };
  }, [pathname]);

  return null;
}
